/**
 * The site's primary wayfinding destinations — shared by the top `FieldNav`
 * links and `FieldFooter`'s link list, so the two can never drift apart on
 * wording or ordering. Deliberately NOT shared with the mobile `Drawer`
 * menu, whose list mixes in CTA-style actions ("Quiz Studio", "Take the
 * placement test") that aren't pure wayfinding.
 *
 * Order follows the product funnel (play → build skills → goal-directed
 * prep → social → pricing), with Plans last.
 */
export interface NavLink {
  to: string;
  label: string;
}

export const NAV_LINKS: NavLink[] = [
  { to: "/games", label: "Games" },
  { to: "/practice", label: "Practice" },
  { to: "/test-prep", label: "Test Prep" },
  // The classroom funnel's one consumer-facing door: a chooser that sorts
  // teachers (→ /teachers) from students (→ /join) and auto-routes anyone
  // already signed in. Deliberately a single neutral word — the main buyers
  // here are parents, and "Students"/"For teachers" links would read as
  // school software.
  { to: "/homework", label: "Homework" },
  { to: "/community", label: "Community" },
  { to: "/blog", label: "Blog" },
  { to: "/plans", label: "Plans" },
];

/**
 * Deep-link columns for the fat FieldFooter. Rendered on every Field Day page,
 * this is the site's main crawl spine into the deep grade / test pages (the
 * footer CSS grid already existed but only the legal row was being rendered, so
 * grade hubs and per-test pages had no site-wide inbound link). Every target is
 * a real prerendered page; keep this list to evergreen, high-intent destinations
 * so the footer stays a curated map rather than an exhaustive dump.
 */
export interface FooterColumn {
  title: string;
  links: NavLink[];
}

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    title: "Practice",
    links: [
      { to: "/practice", label: "Math practice by grade" },
      { to: "/practice/grade-3", label: "Grade 3 math" },
      { to: "/practice/grade-4", label: "Grade 4 math" },
      { to: "/practice/grade-5", label: "Grade 5 math" },
      { to: "/practice/grade-6", label: "Grade 6 math" },
      { to: "/practice/grade-7", label: "Grade 7 math" },
      { to: "/practice/grade-8", label: "Grade 8 math" },
      { to: "/standards", label: "Common Core standards" },
    ],
  },
  {
    title: "Test prep",
    links: [
      { to: "/test-prep", label: "All test prep" },
      // /test-prep/all is the crawlable spine to every state-test hub (and
      // through them the ~1,400 coverage leaves); before this footer link its
      // only inbound link site-wide was one tile on /test-prep.
      { to: "/test-prep/all", label: "Every state test" },
      { to: "/sat", label: "SAT math prep" },
      { to: "/act", label: "ACT math prep" },
      { to: "/staar", label: "STAAR math prep" },
      { to: "/map", label: "MAP math prep" },
      { to: "/iready", label: "i-Ready math prep" },
      { to: "/sbac", label: "SBAC math prep" },
    ],
  },
  {
    title: "Explore",
    links: [
      { to: "/games", label: "Math games" },
      { to: "/teachers", label: "Homework for teachers" },
      { to: "/community", label: "Community quizzes" },
      { to: "/features", label: "Features" },
      { to: "/compare", label: "How we compare" },
      { to: "/blog", label: "Blog" },
      { to: "/plans", label: "Plans & pricing" },
      { to: "/trust", label: "Trust & safety" },
      { to: "/about", label: "About us" },
    ],
  },
];
